import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const PAYMENT_STATUSES = ["Paid", "Pending", "Failed", "Refunded"];
const ORDER_STATUSES = [
  "placed",
  "packed",
  "shipped",
  "out for delivery",
  "delivered",
  "cancelled",
];

const statusColors = {
  delivered: "bg-green-100 text-green-800",
  shipped: "bg-blue-100 text-blue-800",
  packed: "bg-yellow-100 text-yellow-800",
  placed: "bg-indigo-100 text-indigo-800",
  "out for delivery": "bg-purple-100 text-purple-800",
  cancelled: "bg-red-100 text-red-800",
};

const paymentColors = {
  Paid: "bg-green-100 text-green-800",
  Pending: "bg-yellow-100 text-yellow-800",
  Failed: "bg-red-100 text-red-800",
  Refunded: "bg-blue-100 text-blue-800",
};

const OrdersPage = () => {
  const adminToken = localStorage.getItem("adminToken");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6; // ✅ compact to avoid vertical scroll

  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/orders`, {
        headers: { Authorization: adminToken ? `Bearer ${adminToken}` : "" },
      });

      const ct = res.headers.get("content-type") || "";
      const data = ct.includes("application/json")
        ? await res.json()
        : await res.text();

      if (!res.ok)
        throw new Error(data?.message || data?.error || "Failed to fetch orders");

      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, []);

  const updateOrder = async (orderNumber, patch) => {
    setUpdatingId(orderNumber);
    try {
      const res = await fetch(`${API}/api/admin/orders/${orderNumber}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: adminToken ? `Bearer ${adminToken}` : "",
        },
        body: JSON.stringify(patch),
      });

      const ct = res.headers.get("content-type") || "";
      const data = ct.includes("application/json")
        ? await res.json()
        : await res.text();

      if (!res.ok)
        throw new Error(data?.message || data?.error || "Update failed");

      setOrders((prev) =>
        prev.map((o) => (o.id === orderNumber ? { ...o, ...patch } : o))
      );
      toast.success("Order updated");
    } catch (e) {
      toast.error(e.message);
      fetchOrders();
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const matchesSearch = String(order.id || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "All" || order.orderStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [orders, searchTerm, statusFilter]);

  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  }, [filteredOrders]);

  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedOrders.slice(start, start + itemsPerPage);
  }, [sortedOrders, currentPage]);

  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);

  const uniqueStatuses = useMemo(() => {
    return ["All", ...new Set(orders.map((o) => o.orderStatus).filter(Boolean))];
  }, [orders]);

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString("en-IN");
    } catch {
      return "-";
    }
  };

  const shortAddress = (order) => {
    const addr = order?.deliveryAddress || "";
    if (!addr) return "-";
    return addr.length > 18 ? addr.slice(0, 18) + "..." : addr;
  };

  const fullAddress = (order) => {
    const a = order?.address || {};
    return [a.line1, a.line2, a.city, a.state, a.zip]
      .filter(Boolean)
      .join(", ");
  };

  return (
    <div className="container mx-auto px-4 py-8 ">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <h1 className="text-3xl font-bold">Admin Orders</h1>
        <button
          onClick={fetchOrders}
          className="px-4 py-2 border rounded hover:bg-gray-50 disabled:opacity-50 w-fit"
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <input
          type="text"
          placeholder="Search by Order ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md flex-1"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md md:w-[220px]"
        >
          {uniqueStatuses.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      {loading && <p className="text-gray-600">Loading orders...</p>}

      <div className="hidden md:block bg-white rounded-xl border overflow-hidden">
        <table className="w-full table-fixed border-collapse text-sm">
          <colgroup>
            <col className="w-[14%]" /> {/* Order */}
            <col className="w-[16%]" /> {/* Customer */}
            <col className="w-[10%]" />  {/* Items */}
            <col className="w-[10%]" /> {/* Amount */}
            <col className="w-[16%]" /> {/* Pay Status */}
            <col className="w-[16%]" /> {/* Order Status */}
            <col className="w-[10%]" /> {/* Order Date */}
            <col className="w-[8%]" />  {/* Address */}
            <col className="w-[6%]" />  {/* Track */}
          </colgroup>

          <thead>
            <tr className="bg-gray-50 border-b">
              {[
                "Order",
                "Customer",
                "Product Name",
                "Amount",
                "Pay Status",
                "Order Status",
                "Detailed View",
                "Track",
              ].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left text-[11px] font-bold tracking-wider text-gray-700 uppercase"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {paginatedOrders.map((order) => {
              const isUpdating = updatingId === order.id;
              const orderStatusKey = String(order.orderStatus || "").toLowerCase();
              const paymentStatusKey = String(order.paymentStatus || "");

              return (
                <tr key={order.id} className="border-t hover:bg-gray-50 align-top">
                  <td className="px-3 py-2 text-sm break-words whitespace-normal">{order.id}</td>

                  <td className="px-3 py-2 text-sm break-words whitespace-normal">
                    {order.customerName || "-"}
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {order.customerPhone || "-"}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-sm break-words whitespace-normal">{order.productNames}</td>
                  {/* <td className="px-3 py-2 text-sm break-words whitespace-normal">{order.totalItems ?? "-"}</td> */}
                  {/* <td className="px-2 py-2 text-sm break-words whitespace-normal">{order.category || "-"}</td> */}
                  <td className="px-3 py-2 font-semibold">
                    ₹{Number(order.totalAmount || 0).toFixed(2)}
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {order.paymentMode || "-"}
                    </div>
                  </td>

                  <td className="px-3 py-2 text-sm break-words whitespace-normal">
                    <div className="flex flex-col gap-1.5">
                      <select
                        value={order.paymentStatus}
                        disabled={isUpdating}
                        onChange={(e) =>
                          updateOrder(order.id, { paymentStatus: e.target.value })
                        }
                        className="w-full min-w-[120px] px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900
                                    focus:outline-none focus:border-black focus:ring-2 focus:ring-black/20
                                    disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {PAYMENT_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>

                      <span
                        className={`inline-flex w-fit px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          paymentColors[paymentStatusKey] ||
                          "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {order.paymentStatus || "-"}
                      </span>
                    </div>
                  </td>

                  <td className="px-3 py-2 text-sm break-words whitespace-normal">
                    <div className="flex flex-col gap-1.5">
                      <select
                        value={order.orderStatus}
                        disabled={isUpdating}
                        onChange={(e) =>
                          updateOrder(order.id, { orderStatus: e.target.value })
                        }
                        className="w-full min-w-[120px] px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900
                                  focus:outline-none focus:border-black focus:ring-2 focus:ring-black/20
                                  disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {ORDER_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>

                      <span
                        className={`inline-flex w-fit px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          statusColors[orderStatusKey] ||
                          "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {order.orderStatus || "-"}
                      </span>
                    </div>
                  </td>

                  {/* <td className="px-3 py-2">{formatDate(order.date)}</td> */}

                  <td className="px-3 py-2 text-sm break-words whitespace-normal">
                    {/* <div className="">{shortAddress(order)}</div> */}
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="text-blue-600 text-sm hover:underline mt-3"
                    >
                      View
                    </button>
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500">
                    {order.trackingUrl ? (
                      <a
                        href={order.trackingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Open
                      </a>
                    ) : (
                      <span>Add later</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile view - stacked cards */}
<div className="block md:hidden space-y-4">
  {paginatedOrders.map((order) => (
    <div
      key={order.id}
      className="bg-white rounded-xl border shadow-sm p-4"
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-sm text-gray-800">
          Order #{order.id}
        </h3>
        <span
          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${
            statusColors[String(order.orderStatus || "").toLowerCase()] ||
            "bg-gray-100 text-gray-700"
          }`}
        >
          {order.orderStatus || "-"}
        </span>
      </div>

      {/* Divider */}
      <div className="border-t my-3" />

      {/* Customer */}
      <div className="flex justify-between items-center text-sm mb-3">
        <span className="text-gray-600 font-medium">Customer</span>
        <button
          onClick={() => setSelectedOrder(order)}
          className="text-blue-600 text-xs font-semibold hover:underline"
        >
          View details
        </button>
      </div>

      {/* Status Controls */}
      <div className="grid grid-cols-1 gap-3">
        {/* Payment Status */}
        <div>
          <label className="text-xs font-semibold text-gray-600 block mb-1">
            Payment Status
          </label>
          <select
            value={order.paymentStatus}
            disabled={updatingId === order.id}
            onChange={(e) =>
              updateOrder(order.id, { paymentStatus: e.target.value })
            }
            className="w-full px-3 py-2.5 text-sm border rounded-lg bg-white
                       focus:outline-none focus:ring-2 focus:ring-black/20
                       disabled:opacity-60"
          >
            {PAYMENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* Order Status */}
        <div>
          <label className="text-xs font-semibold text-gray-600 block mb-1">
            Order Status
          </label>
          <select
            value={order.orderStatus}
            disabled={updatingId === order.id}
            onChange={(e) =>
              updateOrder(order.id, { orderStatus: e.target.value })
            }
            className="w-full px-3 py-2.5 text-sm border rounded-lg bg-white
                       focus:outline-none focus:ring-2 focus:ring-black/20
                       disabled:opacity-60"
          >
            {ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t my-3" />

      {/* Tracking */}
      <div className="flex justify-between items-center text-sm">
        <span className="text-gray-600 font-medium">Tracking</span>
        {order.trackingUrl ? (
          <a
            href={order.trackingUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 text-xs font-semibold hover:underline"
          >
            Open
          </a>
        ) : (
          <span className="text-xs text-gray-400">Not added</span>
        )}
      </div>
    </div>
  ))}
</div>



      {totalPages > 1 && (
        <div className="mt-8 flex justify-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {!loading && sortedOrders.length === 0 && (
        <p className="text-center text-gray-600 mt-8">No orders found.</p>
      )}

      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-lg">
            <div className="flex items-start justify-between">
              <h2 className="text-xl font-bold">Order Details</h2>
              <button
                onClick={() => setSelectedOrder(null)}
                className="px-3 py-1 border rounded"
              >
                Close
              </button>
            </div>

            <div className="mt-4 text-sm space-y-3">
              <p><b>Customer:</b> {selectedOrder.customerName}</p>
              <p><b>Phone:</b> {selectedOrder.customerPhone}</p>
              <p><b>Product name:</b> {selectedOrder.productNames}</p>
              <p><b>Category:</b> {selectedOrder.category || "-"}</p>
              <p><b>Items:</b> {selectedOrder.totalItems}</p>
              <p><b>Address:</b> {fullAddress(selectedOrder)}</p>
              <p><b>Payment:</b> {selectedOrder.paymentMode}</p>
              <p><b>Status:</b> {selectedOrder.orderStatus}</p>
              <p><b>Date:</b> {formatDate(selectedOrder.date)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
